import React from 'react';
import ScenarioAvatar from './ScenarioAvatar';
import './ScenarioCard.css';

/**
 * ScenarioCard — the reusable J3 shortlist card (#1921), exported per the
 * #1921 blocking precondition (2026-07-31 comment) so #1923's learner-home
 * variant can reuse it rather than rebuilding it. Also the card the #1922
 * chooser's outcome list renders.
 *
 * Badges are duration + prep ONLY — gate decision, do not add more (no org
 * pills for guests, no ranking language, no modality-lock language). The
 * prep badge is deliberately LOUD (gate decision): gold "Read first" when
 * the scenario has a pre-read artifact, green "No prep" otherwise — never a
 * quiet grey chip. Fix round 2026-08-24 (Gilbert): the "Voice available"
 * pill is DROPPED — voice is near-universal (158/160 at the gate
 * measurement) so the pill carried zero information; `card.voice_available`
 * stays in the payload and is still consumed by CommitmentSheet's #2089
 * text_only gate, just not rendered as a badge here.
 *
 * Every card always renders its avatar (fix round 2026-08-24: "every
 * scenario card always renders its avatar image") via the shared
 * `ScenarioAvatar`, which falls back to an initials monogram when
 * `card.avatar_url` is null.
 *
 * Pure presentational — no fetching, no analytics. The container wires
 * `onSelect`.
 */
function ScenarioCard({ card, onSelect }) {
  const durationMinutes = card.median_first_run_duration_minutes;
  const hasDuration = typeof durationMinutes === 'number' && durationMinutes > 0;

  return (
    <button
      type="button"
      className="gd-scenario-card"
      data-testid={`gd-scenario-card-${card.id}`}
      onClick={() => onSelect(card)}
    >
      <div className="gd-scenario-card__header">
        <ScenarioAvatar avatarUrl={card.avatar_url} name={card.ai_role || card.name} size="md" />
        <h3 className="gd-scenario-card__title">{card.name}</h3>
      </div>
      {card.summary && <p className="gd-scenario-card__summary">{card.summary}</p>}
      <div className="gd-scenario-card__badges">
        {hasDuration && (
          <span className="gd-badge gd-badge--neutral">
            <span aria-hidden="true">⏱</span> {durationMinutes} min
          </span>
        )}
        {card.prep_required ? (
          <span className="gd-badge gd-badge--prep">
            <span aria-hidden="true">📄</span> Read first
          </span>
        ) : (
          <span className="gd-badge gd-badge--no-prep">
            <span aria-hidden="true">✓</span> No prep
          </span>
        )}
      </div>
    </button>
  );
}

export default ScenarioCard;
